import React, { useEffect, useState } from 'react'

import { Box, BoundedContent, SkeletonText, SkeletonTextLine, useScreenClass, FlexBox } from '@vp/swan'
import { ShippingInformation } from '@vp/lat-react-component-library'
import { PricePresentation, PricePresentationType } from '@vp/price-platform'

import QuantityButtons from './QuantityButtons'
import { usePricingContext } from '../../root/contexts/PricingContext'
import { useWorksDataContext } from '../../root/contexts/WorksDataContext'
import { useAppState } from '../../root/contexts/AppContext'
import type { WorkData } from '../../../types/Work'
import { getIsFreeDelivery } from '../../../utils/ShippingInformationHelpers'
import { buildPricesForPricePresentation, getTotalQuantity } from '../../../utils/WebsitePricingHelpers'
import useQuotePrice from '../../../hooks/queries/useQuotePrice'
import useDebounce from '../../../hooks/useDebounce'
import { CarouselLink } from '../../shared/CarouselLink'
import { EXPERIMENTS, useABTest } from '../../root/contexts/ABTestContext'

export const QuantityFooter: React.FC = () => {
  const { pricingContextModule } = usePricingContext()
  const { worksData } = useWorksDataContext()
  const { culture } = useAppState()
  const screenClass = useScreenClass()
  const { currentExperiments } = useABTest()

  const [isPriceChangeLoading, setIsPriceChangeLoading] = useState(false)
  const debouncedWorksDataForPricing = useDebounce(worksData)
  const {
    data: productQuotePrice,
    isLoading: isLoadingQuotePrice,
    isFetched: quotePriceIsFetched,
  } = useQuotePrice(debouncedWorksDataForPricing as Record<string, WorkData>)

  const isMobile = screenClass === 'xs'

  useEffect(() => {
    if (isLoadingQuotePrice || debouncedWorksDataForPricing !== worksData) {
      setIsPriceChangeLoading(true)
    } else {
      setIsPriceChangeLoading(false)
    }
  }, [isLoadingQuotePrice, debouncedWorksDataForPricing])

  const totalQuantity = getTotalQuantity(productQuotePrice)
  return (
    <Box className="quantity-footer">
      <BoundedContent className="footer-bounded-content">
        <FlexBox justifyContent="flex-end" gap={screenClass === 'xs' ? 0 : 4} flexDirection={screenClass === 'xs' ? 'column' : 'inherit'}>
          <Box className="quantity-footer-info" pr={4} mx={screenClass === 'xs' ? 3 : 0}>
            {isMobile && (
              <CarouselLink
                hasColorOptions={
                  EXPERIMENTS['ADD_OTHER_COLORS'] &&
                  EXPERIMENTS['ADD_OTHER_COLORS'].variations['TEST'] === currentExperiments[EXPERIMENTS['ADD_OTHER_COLORS'].key]
                }
              />
            )}
            {!isPriceChangeLoading && quotePriceIsFetched && productQuotePrice ? (
              <>
                {totalQuantity > 0 && (
                  <>
                    <PricePresentation
                      presentationType={PricePresentationType.PriceForQtyUnits}
                      prices={buildPricesForPricePresentation(productQuotePrice)}
                      priceProperties={{
                        minimumFractionDigits: productQuotePrice?.fractionDigits || 2,
                        locale: culture,
                        currency: productQuotePrice?.currency,
                        vatInc: pricingContextModule.vatInclusive,
                        quantity: totalQuantity,
                        hideVatMessage: false,
                      }}
                    />
                    <ShippingInformation isLink={false} shippingInformation="" isFreeDelivery={getIsFreeDelivery(culture)} />
                  </>
                )}
              </>
            ) : (
              <SkeletonText style={{ width: '200px' }}>
                <SkeletonTextLine></SkeletonTextLine>
                <SkeletonTextLine></SkeletonTextLine>
              </SkeletonText>
            )}
          </Box>
          <Box className="quantity-footer-add" mt={screenClass === 'xs' ? 'between-actions' : 0}>
            <QuantityButtons pb={4} mt={{ sm: 4 }} />
          </Box>
        </FlexBox>
      </BoundedContent>
    </Box>
  )
}
